/*
 * Energy widgets in the look of the TP8 demo (tp8-visu, page Energie):
 * power flow with the coefficient of performance in a ring, twelve monthly
 * bars with a reference line, and a split bar for electricity against heat.
 */

import { useEffect } from 'preact/hooks';
import type { MonthBarsWidget, PowerFlowWidget, SplitBarWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isNoSensor } from './PanelWidgets';
import './energy.css';

interface P<W> { widget: W; source: IPointBindingSource; }

function useRaw(source: IPointBindingSource, key: string | null | undefined): unknown {
  const sig = key ? pointSignal(source, key) : null;
  const release = sig?.release;
  useEffect(() => release, [release]);
  return sig?.value.value?.value;
}

function useNum(source: IPointBindingSource, key: string | null | undefined): number | undefined {
  const raw = useRaw(source, key);
  return typeof raw === 'number' && !isNoSensor(raw) ? raw : undefined;
}

/** Mehrere Punkte auf einmal. Die Liste ist je Widget fest, die Zahl der
 *  Hook-Aufrufe bleibt damit von Render zu Render gleich. */
function useNums(source: IPointBindingSource, keys: readonly string[] | undefined): (number | undefined)[] {
  return (keys ?? []).map((k) => useNum(source, k));
}

/** Summe, sobald mindestens ein Wert da ist; sonst undefined. */
function sum(values: (number | undefined)[]): number | undefined {
  const known = values.filter((v): v is number => v !== undefined);
  return known.length ? known.reduce((x, y) => x + y, 0) : undefined;
}

/** Arbeitszahl je Monat aus Waerme und Strom; ohne Strom kein Balken. */
export function monthlyFactors(heat: (number | undefined)[], el: (number | undefined)[]): (number | null)[] {
  return Array.from({ length: 12 }, (_, i) => {
    const h = heat[i]; const e = el[i];
    return h !== undefined && e !== undefined && e > 0 ? h / e : null;
  });
}

const f1 = (v: number | undefined) => (v === undefined ? '–'
  : v.toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const f0 = (v: number | undefined) => (v === undefined ? '–'
  : v.toLocaleString('de-DE', { maximumFractionDigits: 0 }));

// ------------------------------------------------------------ power flow

export function PowerFlowRenderer({ widget, source }: P<PowerFlowWidget>) {
  const el = useNum(source, widget.elPoint);
  const heat = useNum(source, widget.heatPoint);
  const copRaw = useNum(source, widget.copPoint);
  const cop = copRaw ?? (el && heat ? heat / el : undefined);
  const running = (heat ?? 0) > 0.05;
  const C = 2 * Math.PI * 46;
  const ring = Math.min(1, (cop ?? 0) / 6);
  return (
    <div class={`hv-eflow${running ? ' hv-eflow--run' : ''}`}>
      <div class="hv-eflow__side">
        <span class="hv-eflow__label">Strom</span>
        <span class="hv-eflow__value">{f1(el)}<small>kW</small></span>
        <span class="hv-eflow__src hv-eflow__src--el">{widget.elSource ?? 'Stromzähler'}</span>
      </div>
      <div class="hv-eflow__seg hv-eflow__seg--in" />
      <div class="hv-eflow__cop">
        <svg viewBox="0 0 110 110" aria-hidden="true">
          <circle cx="55" cy="55" r="46" class="hv-eflow__ring-bg" stroke-width="8" />
          {cop !== undefined && (
            <>
              <circle cx="55" cy="55" r="46" fill="none" class="hv-eflow__ring-el" stroke-width="8" stroke-linecap="round"
                stroke-dasharray={`${C * ring * 0.35} ${C}`} transform="rotate(-90 55 55)" />
              <circle cx="55" cy="55" r="46" fill="none" class="hv-eflow__ring-heat" stroke-width="8" stroke-linecap="round"
                stroke-dasharray={`${C * ring * 0.65} ${C}`} stroke-dashoffset={-C * ring * 0.35} transform="rotate(-90 55 55)" />
            </>
          )}
        </svg>
        <span class="hv-eflow__cop-v">{cop === undefined ? '–' : f1(cop)}</span>
        <span class="hv-eflow__cop-l">Arbeitszahl</span>
      </div>
      <div class="hv-eflow__seg hv-eflow__seg--out" />
      <div class="hv-eflow__side hv-eflow__side--r">
        <span class="hv-eflow__label">Wärme</span>
        <span class="hv-eflow__value">{f1(heat)}<small>kW</small></span>
        <span class="hv-eflow__src hv-eflow__src--heat">{widget.heatSource ?? 'Wärmemengenzähler'}</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ month bars

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

export function MonthBarsRenderer({ widget, source }: P<MonthBarsWidget>) {
  const raw = useRaw(source, widget.valuesPoint);
  const refPoint = useNum(source, widget.referencePoint);
  const heat = useNums(source, widget.heatPoints);
  const el = useNums(source, widget.elPoints);
  const fromCounters = heat.length === 12 && el.length === 12;
  const heatSum = sum(heat); const elSum = sum(el);
  const ref = refPoint ?? (fromCounters && heatSum !== undefined && elSum ? heatSum / elSum : undefined);
  const values = fromCounters
    ? monthlyFactors(heat, el)
    : Array.isArray(raw) ? raw.map((v) => (typeof v === 'number' ? v : null)) : null;
  const max = widget.max ?? 5.5;
  const cur = new Date().getMonth();
  const pct = (v: number) => `${Math.min(100, Math.max(0, (v / max) * 100))}%`;
  return (
    <div class="hv-months">
      {widget.title && <span class="hv-months__title">{widget.title}</span>}
      {!values ? <div class="hv-months__none">Monatswerte folgen</div> : (
        <div class="hv-months__plot">
          {ref !== undefined && (
            <div class="hv-months__ref" style={{ bottom: pct(ref) }}>
              <span>{widget.referenceLabel ?? 'JAZ'} {f1(ref)}</span>
            </div>
          )}
          {MONTHS.map((m, i) => {
            const v = values[i];
            return (
              <div key={i} class="hv-months__col">
                <div class="hv-months__wrap">
                  {v !== null && v !== undefined
                    ? <div class={`hv-months__bar${i === cur ? ' hv-months__bar--cur' : ''}`} style={{ height: pct(v) }}>
                        {i === cur && <em>{f1(v)}</em>}
                      </div>
                    : <div class="hv-months__bar hv-months__bar--empty" />}
                </div>
                <span class={i === cur ? 'hv-months__m hv-months__m--cur' : 'hv-months__m'}>{m}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ split bar

export function SplitBarRenderer({ widget, source }: P<SplitBarWidget>) {
  const aOne = useNum(source, widget.aPoint);
  const bOne = useNum(source, widget.bPoint);
  const aList = useNums(source, widget.aPoints);
  const bList = useNums(source, widget.bPoints);
  const a = widget.aPoints?.length ? sum(aList) : aOne;
  const b = widget.bPoints?.length ? sum(bList) : bOne;
  const unit = widget.unit ?? 'kWh';
  const share = a !== undefined && b !== undefined && b > 0 ? Math.min(1, a / b) : 0.3;
  const ratio = a && b ? b / a : undefined;
  return (
    <div class="hv-split">
      <div class="hv-split__top">
        <span class="hv-split__title">{widget.title ?? 'Dieses Jahr'}</span>
        {ratio !== undefined && <span class="hv-split__ratio">aus 1 kWh Strom wurden {f1(ratio)} kWh Wärme</span>}
      </div>
      <div class="hv-split__bar">
        <div class="hv-split__a" style={{ width: `${share * 100}%` }} />
        <div class="hv-split__b" />
      </div>
      <div class="hv-split__legend">
        <span><i class="hv-split__dot hv-split__dot--a" />{widget.aLabel ?? 'Strom'} <b>{f0(a)} {unit}</b></span>
        <span><i class="hv-split__dot hv-split__dot--b" />{widget.bLabel ?? 'Wärme'} <b>{f0(b)} {unit}</b></span>
      </div>
    </div>
  );
}
